import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import {
  FileText, ShieldCheck, Users, AlertTriangle, Scale,
  Mail, Phone, ArrowLeft, CreditCard
} from 'lucide-react';

export default function TermsAndConditionsPage() {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'Acceptance of Terms',
      icon: ShieldCheck,
      content: [
        {
          subtitle: 'Agreement',
          text: 'By using the Scratch Unleashed website, mobile site, or by booking, checking-in, or visiting our venue, you accept and agree to be bound by these Terms & Conditions, our Privacy Policy, and our marketing communication preferences. If you do not agree, please do not use our services.'
        },
        {
          subtitle: 'Eligibility',
          text: 'You must be 21 years or older to access the Scratch Unleashed venue and to consume alcoholic beverages. By creating an account or checking in, you confirm that you meet this minimum age requirement. We may request valid government-issued photo identification at entry.'
        }
      ]
    },
    {
      title: 'Membership & Accounts',
      icon: Users,
      content: [
        {
          subtitle: 'Automatic Membership on Check-In',
          text: "When you complete a guest check-in, a Scratch Unleashed member account is automatically created for you using the phone number you provided. You can later log in using OTP (sent via WhatsApp or SMS) from our homepage and complete your profile—add an email address, set a password, and manage your preferences."
        },
        {
          subtitle: 'Account Security',
          text: 'You are responsible for keeping your account credentials, OTPs, and registered phone number secure. Do not share OTPs with anyone. We may suspend or terminate accounts in cases of suspected fraud, misuse, or violation of these Terms.'
        },
        {
          subtitle: 'Accurate Information',
          text: 'You agree to provide accurate, current, and complete information during sign-up, check-in, and bookings. Providing false information may result in refusal of entry, cancellation of bookings without refund, and account suspension.'
        }
      ]
    },
    {
      title: 'Bookings, Check-In & Cancellations',
      icon: FileText,
      content: [
        {
          subtitle: 'Reservations',
          text: 'Table reservations and VIP bookings are subject to availability and at the discretion of Scratch Unleashed management. A confirmed booking does not guarantee admission if the venue is at capacity, if the guest fails dress code or age verification, or if the guest is in violation of venue policies.'
        },
        {
          subtitle: 'Cover Charges & Cancellations',
          text: 'Cover charges, VIP minimums, and event-specific pricing are displayed at the time of booking. Cancellations made less than 24 hours before the booking date may not be refundable. No refunds will be issued for no-shows.'
        },
        {
          subtitle: 'Dress Code & Conduct',
          text: 'Smart casual dress code is enforced. Management reserves the right to refuse entry for any guest not adhering to the dress code or displaying disruptive behavior. Any guest causing disturbance, damaging property, or harassing staff or other guests will be removed without refund and may face legal action.'
        }
      ]
    },
    {
      title: 'Payments & Refunds',
      icon: CreditCard,
      content: [
        {
          subtitle: 'Payment Processing',
          text: 'All payments are processed securely through our payment partners (Paytm UPI / authorised gateways). Scratch Unleashed does not store complete payment card information on its servers.'
        },
        {
          subtitle: 'Refund Policy',
          text: 'Eligible refunds (cancellations 24+ hours in advance, venue-initiated cancellations, or duplicate charges) will be processed to the original payment method within 7–10 business days. Service fees and payment gateway charges are non-refundable.'
        }
      ]
    },
    {
      title: 'Marketing Communications',
      icon: Mail,
      content: [
        {
          subtitle: 'Consent',
          text: 'By accepting these Terms, you consent to receive transactional and promotional communications from Scratch Unleashed via WhatsApp, SMS, email, and phone calls. This includes booking confirmations, event invitations, exclusive offers, birthday greetings, and product updates.'
        },
        {
          subtitle: 'Opt-Out',
          text: 'You can opt out of marketing communications at any time by replying STOP to any SMS, clicking unsubscribe in emails, updating your preferences in your account profile, or contacting us directly at privacy@scratchjsr.com. Transactional communications (e.g., booking confirmations, OTPs) will continue as they are essential to service delivery.'
        }
      ]
    },
    {
      title: 'Liability & Disclaimers',
      icon: AlertTriangle,
      content: [
        {
          subtitle: 'Venue Liability',
          text: "Scratch Unleashed is not liable for loss, theft, or damage to personal belongings on its premises. Guests are responsible for safeguarding their personal items, including phones, wallets, and accessories. Coat-check and locker services, if available, are used at the guest's own risk."
        },
        {
          subtitle: 'Health & Safety',
          text: 'Guests participate in venue activities at their own risk. Scratch Unleashed is not liable for any health issues, injuries, or incidents arising from alcohol consumption, dancing, or other activities. If you have medical conditions or allergies, please inform our staff before placing food or beverage orders.'
        },
        {
          subtitle: 'Service Availability',
          text: 'We strive to provide uninterrupted service but do not guarantee that the website, booking systems, OTP delivery, or venue operations will always be available without delays or errors. We are not liable for losses arising from temporary service unavailability.'
        }
      ]
    },
    {
      title: 'Intellectual Property & Media',
      icon: Scale,
      content: [
        {
          subtitle: 'Brand Assets',
          text: 'The Scratch Unleashed name, logo, branding, website content, and all related intellectual property are owned by Scratch Unleashed. Unauthorized reproduction, distribution, or commercial use is strictly prohibited.'
        },
        {
          subtitle: 'Photography & Filming',
          text: 'Scratch Unleashed may photograph or film events for promotional purposes. By entering the venue, you consent to being recorded and to the use of your image in our marketing materials. If you do not wish to be photographed, please inform our staff at entry.'
        },
        {
          subtitle: 'Governing Law',
          text: 'These Terms are governed by the laws of India and any disputes arising from them will be subject to the exclusive jurisdiction of the courts at Jamshedpur, Jharkhand. We reserve the right to update these Terms from time to time; continued use of our services after updates constitutes acceptance of the revised Terms.'
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Terms &amp; Conditions | Scratch Unleashed</title>
        <meta
          name="description"
          content="Read the Terms & Conditions for using Scratch Unleashed — bookings, memberships, payments, marketing communications, conduct, and more."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://scratchjsr.com/terms-and-conditions" />
        <meta property="og:title" content="Terms & Conditions | Scratch Unleashed" />
        <meta
          property="og:description"
          content="Terms governing your use of Scratch Unleashed bookings, accounts, payments, and venue access."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://scratchjsr.com/terms-and-conditions" />
      </Helmet>

      <div data-testid="terms-page" className="min-h-screen" style={{ backgroundColor: '#0A0A0A' }}>
        {/* Back Button */}
        <div className="fixed top-6 left-6 z-50">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            data-testid="terms-back-button"
            className="gap-2 text-[#B9B9B9] hover:text-[#F2F2F2]"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Button>
        </div>

        {/* Hero */}
        <section className="relative pt-32 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6"
              style={{ background: 'rgba(212,168,67,0.12)', border: '1px solid rgba(212,168,67,0.25)' }}
            >
              <FileText size={32} style={{ color: '#D4A843' }} />
            </div>
            <h1 className="font-['EB_Garamond'] text-5xl md:text-6xl font-bold text-[#F2F2F2] mb-4">
              Terms &amp; Conditions
            </h1>
            <p className="text-lg text-[#B9B9B9] max-w-2xl mx-auto">
              The rules of engagement for booking, checking in, and being a Scratch Unleashed member.
            </p>
            <p className="text-sm text-[#6B6B6B] mt-4">
              <strong>Last Updated:</strong> January 2026
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="pb-20 px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {sections.map((section, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg" style={{ background: 'rgba(212,168,67,0.1)' }}>
                    <section.icon size={20} style={{ color: '#D4A843' }} />
                  </div>
                  <h2 className="font-['EB_Garamond'] text-2xl font-bold text-[#F2F2F2]">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-6">
                  {section.content.map((item, i) => (
                    <div key={i}>
                      {item.subtitle && (
                        <h3 className="text-lg font-semibold text-[#D4A843] mb-2">
                          {item.subtitle}
                        </h3>
                      )}
                      <p className="text-[#B9B9B9] leading-relaxed whitespace-pre-line">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Quick Actions */}
            <div className="grid md:grid-cols-2 gap-6">
              <a
                href="mailto:hello@scratchjsr.com"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#D4A843] transition-all group text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                <Mail size={24} className="mx-auto mb-3 text-[#D4A843]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">Email Us</h3>
                <p className="text-sm text-[#B9B9B9]">hello@scratchjsr.com</p>
              </a>
              <a
                href="https://wa.me/917870369666"
                target="_blank"
                rel="noopener noreferrer"
                className="p-6 rounded-xl border border-[rgba(255,255,255,0.07)] hover:border-[#25D366] transition-all group text-center"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                <Phone size={24} className="mx-auto mb-3 text-[#25D366]" />
                <h3 className="font-semibold text-[#F2F2F2] mb-1">WhatsApp</h3>
                <p className="text-sm text-[#B9B9B9]">+91 7870 369 666</p>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-[rgba(255,255,255,0.07)] py-8">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-sm text-[#6B6B6B]">
              © {new Date().getFullYear()} Scratch Unleashed. All rights reserved.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <button
                onClick={() => navigate('/privacy-policy')}
                className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors"
              >
                Privacy Policy
              </button>
              <span className="text-[#6B6B6B]">·</span>
              <button
                onClick={() => navigate('/contact')}
                className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors"
              >
                Contact Us
              </button>
              <span className="text-[#6B6B6B]">·</span>
              <a href="https://scratchjsr.com" className="text-sm text-[#B9B9B9] hover:text-[#D4A843] transition-colors">
                scratchjsr.com
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
